import React, { useState } from "react";
import {
  Alert,
  Box,
  Card,
  Chip,
  Container,
  Divider,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import KeyRoundedIcon from "@mui/icons-material/KeyRounded";
import LocalAtmRoundedIcon from "@mui/icons-material/LocalAtmRounded";
import QueueRoundedIcon from "@mui/icons-material/QueueRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { materialDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import BackBtn from "../../widgets/BackBtn";
import useApiEp from "../../hooks/useApiEp";

const cardSx = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 3,
  boxShadow: "0 10px 30px rgba(15, 76, 68, 0.055)",
};

const requestParameters = [
  {
    name: "templateId",
    type: "string",
    required: true,
    description:
      "ID of an approved WhatsApp message template available to your connected account.",
    example: "7667676877878",
  },
  {
    name: "apiCode",
    type: "string",
    required: true,
    description:
      "Your account API code. This is checked in addition to the Bearer token.",
    example: "YOUR_API_CODE",
  },
  {
    name: "campaignName",
    type: "string",
    required: true,
    description: "A descriptive name used to identify the queued messages.",
    example: "September offers",
  },
  {
    name: "phoneNumbers",
    type: "string[]",
    required: true,
    description:
      "Recipients in international format with country code. Send digits only, without + or separators.",
    example: '["919876543210"]',
  },
  {
    name: "headerParams",
    type: "string[]",
    required: false,
    description:
      "Values for a variable text header. The current API uses the first value.",
    example: '["Guru"]',
  },
  {
    name: "bodyParams",
    type: "string[]",
    required: false,
    description:
      "Template body variables in the exact placeholder order defined by the template.",
    example: '["Guru", "25 September"]',
  },
  {
    name: "mediaUrl",
    type: "string",
    required: false,
    description:
      "Public media URL required when the template header is image, video, or document.",
    example: "https://cdn.example.com/offer.jpg",
  },
  {
    name: "filename",
    type: "string",
    required: false,
    description: "Displayed filename for a document-header template.",
    example: "brochure.pdf",
  },
  {
    name: "buttonParams",
    type: "string[]",
    required: false,
    description:
      "Dynamic suffix for a templated URL button. The current API uses the first value.",
    example: '["offer-1048"]',
  },
];

const payloadPresets = [
  {
    key: "standard",
    label: "Text template",
    title: "Text template with variables",
    description:
      "Use headerParams and bodyParams when the approved template contains text placeholders.",
    body: {
      templateId: "7667676877878",
      apiCode: "YOUR_API_CODE",
      campaignName: "September offers",
      phoneNumbers: ["919876543210", "919123456789"],
      headerParams: ["Guru"],
      bodyParams: ["20%", "25 September"],
    },
  },
  {
    key: "media",
    label: "Media header",
    title: "Template with a media header",
    description:
      "Pass a publicly accessible media URL matching the approved header format. Add filename for documents.",
    body: {
      templateId: "7667676877878",
      apiCode: "YOUR_API_CODE",
      campaignName: "New catalogue",
      phoneNumbers: ["919876543210"],
      mediaUrl: "https://cdn.example.com/catalogue.pdf",
      filename: "September-catalogue.pdf",
      bodyParams: ["Guru", "September"],
    },
  },
  {
    key: "authentication",
    label: "Authentication",
    title: "Authentication template",
    description:
      "For authentication templates, the first bodyParams value becomes both the body OTP and URL-button value.",
    body: {
      templateId: "7667676877878",
      apiCode: "YOUR_API_CODE",
      campaignName: "Login verification",
      phoneNumbers: ["919876543210"],
      bodyParams: ["482913"],
    },
  },
  {
    key: "url-button",
    label: "URL button",
    title: "Template with a dynamic URL button",
    description:
      "Use buttonParams when the approved URL button contains a dynamic {{...}} suffix.",
    body: {
      templateId: "7667676877878",
      apiCode: "YOUR_API_CODE",
      campaignName: "Order updates",
      phoneNumbers: ["919876543210"],
      bodyParams: ["Guru", "SG-1048"],
      buttonParams: ["SG-1048"],
    },
  },
];

const errorResponses = [
  [
    "400",
    "Account expired",
    "The connected WhatsApp account has passed its expiry date.",
  ],
  [
    "400",
    "Insufficient balance",
    "The wallet cannot cover all recipients in this request.",
  ],
  [
    "401",
    "Invalid API code",
    "apiCode does not match the account identified by the token.",
  ],
  ["401", "Unauthorized", "The Bearer token is missing, invalid, or expired."],
  [
    "4xx",
    "Template or provider error",
    "The template could not be loaded from the connected provider.",
  ],
  [
    "500",
    "Invalid template parameters",
    "Required OTP or dynamic URL values are missing, or the request could not be prepared.",
  ],
];

const CodeBlock = ({ children, language = "json", label }) => {
  const [copied, setCopied] = useState(false);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Box
      sx={{
        overflow: "hidden",
        border: "1px solid #334155",
        borderRadius: 2.5,
        bgcolor: "#111827",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1, bgcolor: "#1f2937", color: "#cbd5e1" }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            letterSpacing: 0.5,
            textTransform: "uppercase",
          }}
        >
          {label || language}
        </Typography>
        <Tooltip title={copied ? "Copied" : "Copy code"}>
          <IconButton
            size="small"
            onClick={copyCode}
            aria-label="Copy code"
            sx={{ color: copied ? "#4ade80" : "#cbd5e1" }}
          >
            {copied ? (
              <CheckCircleOutlineRoundedIcon fontSize="small" />
            ) : (
              <ContentCopyRoundedIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Stack>
      <SyntaxHighlighter
        language={language}
        style={materialDark}
        customStyle={{
          margin: 0,
          padding: "20px",
          background: "#111827",
          fontSize: "0.82rem",
          lineHeight: 1.65,
        }}
        wrapLongLines
      >
        {children}
      </SyntaxHighlighter>
    </Box>
  );
};

const SectionTitle = ({ id, eyebrow, children, description }) => (
  <Box id={id} sx={{ scrollMarginTop: 24, mb: 2.5 }}>
    {eyebrow && (
      <Typography
        variant="overline"
        color="primary.main"
        sx={{ fontWeight: 800, letterSpacing: 1 }}
      >
        {eyebrow}
      </Typography>
    )}
    <Typography
      component="h2"
      sx={{ fontSize: { xs: 22, sm: 26 }, fontWeight: 750, lineHeight: 1.25 }}
    >
      {children}
    </Typography>
    {description && (
      <Typography color="text.secondary" sx={{ mt: 0.75, maxWidth: 800 }}>
        {description}
      </Typography>
    )}
  </Box>
);

const ParameterTable = ({ rows }) => (
  <TableContainer
    component={Paper}
    variant="outlined"
    sx={{ boxShadow: "none" }}
  >
    <Table sx={{ minWidth: 780 }}>
      <TableHead>
        <TableRow>
          <TableCell>Field</TableCell>
          <TableCell>Type</TableCell>
          <TableCell>Required</TableCell>
          <TableCell>Description</TableCell>
          <TableCell>Example</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.name}>
            <TableCell>
              <Box
                component="code"
                sx={{
                  color: "primary.dark",
                  bgcolor: "rgba(18, 140, 126, 0.08)",
                  borderRadius: 1,
                  px: 0.75,
                  py: 0.35,
                  fontSize: "0.78rem",
                  whiteSpace: "nowrap",
                }}
              >
                {row.name}
              </Box>
            </TableCell>
            <TableCell>{row.type}</TableCell>
            <TableCell>
              <Chip
                size="small"
                label={row.required ? "Required" : "Conditional"}
                color={row.required ? "primary" : "default"}
                variant="outlined"
              />
            </TableCell>
            <TableCell>{row.description}</TableCell>
            <TableCell>
              <Box component="code" sx={{ fontSize: "0.78rem" }}>
                {row.example}
              </Box>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const CampaignApiDoc = () => {
  const [payloadPreset, setPayloadPreset] = useState("standard");
  const [codeLanguage, setCodeLanguage] = useState("curl");
  const {
    data: apiCred,
    isLoading,
    isError,
    error,
  } = useApiEp(`/user/get-api-cred`);

  const campaignEndpoint =
    apiCred?.endpoint?.campaign || "https://api.example.com/send/campaign";
  const selectedPreset =
    payloadPresets.find((preset) => preset.key === payloadPreset) ||
    payloadPresets[0];
  const requestBody = JSON.stringify(selectedPreset.body, null, 2);

  const codeSamples = {
    curl: `curl --request POST '${campaignEndpoint}' \\
  --header 'Authorization: Bearer YOUR_API_TOKEN' \\
  --header 'Content-Type: application/json' \\
  --data '${requestBody}'`,
    javascript: `const response = await fetch("${campaignEndpoint}", {
  method: "POST",
  headers: {
    "Authorization": "Bearer YOUR_API_TOKEN",
    "Content-Type": "application/json"
  },
  body: JSON.stringify(${requestBody})
});

const result = await response.json();

if (!response.ok) {
  throw new Error(result.message || "Campaign request failed");
}

console.log(result);`,
    node: `import axios from "axios";

const { data } = await axios.post(
  "${campaignEndpoint}",
  ${requestBody},
  {
    headers: {
      Authorization: "Bearer YOUR_API_TOKEN",
      "Content-Type": "application/json"
    }
  }
);

console.log(data);`,
  };

  const sidebarLinks = [
    ["overview", "Overview"],
    ["authentication", "Authentication"],
    ["send-campaign", "Send a campaign"],
    ["parameters", "Parameters"],
    ["examples", "Examples"],
    ["template-rules", "Template rules"],
    ["response", "Response"],
    ["errors", "Errors"],
  ];

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 } }}>
      <BackBtn bstyle={{ mb: 1.5 }} />

      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          p: { xs: 3, md: 5 },
          mb: 4,
          borderRadius: 3.5,
          color: "common.white",
          background:
            "linear-gradient(120deg, #063d38 0%, #075e54 52%, #128c7e 100%)",
          boxShadow: "0 18px 48px rgba(7, 94, 84, 0.22)",
          "&::after": {
            content: '""',
            position: "absolute",
            width: 320,
            height: 320,
            right: -90,
            top: -180,
            borderRadius: "50%",
            border: "52px solid rgba(255,255,255,0.06)",
          },
        }}
      >
        <Box sx={{ position: "relative", zIndex: 1, maxWidth: 840 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Chip
              label="REST API"
              size="small"
              sx={{ color: "white", bgcolor: "rgba(255,255,255,0.14)" }}
            />
            <Chip
              label="Template messaging"
              size="small"
              sx={{ color: "white", bgcolor: "rgba(255,255,255,0.14)" }}
            />
          </Stack>
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: 30, sm: 42 },
              fontWeight: 780,
              lineHeight: 1.12,
            }}
          >
            Campaign Messaging API
          </Typography>
          <Typography
            sx={{ mt: 1.5, fontSize: { xs: 16, sm: 18 }, opacity: 0.82 }}
          >
            Queue approved WhatsApp template messages for one or more recipients
            with personalized text, media, OTP, and URL-button values.
          </Typography>
        </Box>
      </Box>

      {isError && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error?.message || "Could not load your API endpoint."} Examples are
          showing a placeholder URL.
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "220px minmax(0, 1fr)" },
          gap: { xs: 3, lg: 5 },
          alignItems: "start",
        }}
      >
        <Box
          component="nav"
          aria-label="On this page"
          sx={{
            display: { xs: "none", lg: "block" },
            position: "sticky",
            top: 24,
            borderLeft: "1px solid",
            borderColor: "divider",
            pl: 2,
          }}
        >
          <Typography
            variant="overline"
            sx={{ fontWeight: 800, color: "text.secondary" }}
          >
            On this page
          </Typography>
          <Stack spacing={0.25} sx={{ mt: 1 }}>
            {sidebarLinks.map(([id, label]) => (
              <Link
                key={id}
                href={`#${id}`}
                underline="none"
                color="text.secondary"
                sx={{
                  py: 0.75,
                  fontSize: "0.875rem",
                  "&:hover": { color: "primary.main" },
                }}
              >
                {label}
              </Link>
            ))}
          </Stack>
        </Box>

        <Stack spacing={6} sx={{ minWidth: 0 }}>
          <Box>
            <SectionTitle
              id="overview"
              eyebrow="Introduction"
              description="Use this endpoint for approved WhatsApp templates, including messages sent outside the 24-hour customer-service window. Each recipient is stored and queued individually for background delivery."
            >
              Overview
            </SectionTitle>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                gap: 2,
              }}
            >
              {[
                [
                  <GroupsRoundedIcon />,
                  "Multiple recipients",
                  "Submit one template payload for a list of phone numbers.",
                ],
                [
                  <CampaignRoundedIcon />,
                  "Approved templates",
                  "The template is loaded from your connected WhatsApp account.",
                ],
                [
                  <QueueRoundedIcon />,
                  "Asynchronous delivery",
                  "A successful response confirms that messages were queued.",
                ],
              ].map(([icon, title, text]) => (
                <Card key={title} elevation={0} sx={{ ...cardSx, p: 2.5 }}>
                  <Box sx={{ color: "primary.main", mb: 1.25 }}>{icon}</Box>
                  <Typography fontWeight={700}>{title}</Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.75 }}
                  >
                    {text}
                  </Typography>
                </Card>
              ))}
            </Box>
          </Box>

          <Box>
            <SectionTitle
              id="authentication"
              eyebrow="Security"
              description="Campaign requests use two account credentials. Both must belong to the same user."
            >
              Authentication
            </SectionTitle>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2,
                mb: 2.5,
              }}
            >
              <Card elevation={0} sx={{ ...cardSx, p: 2.5 }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <SecurityRoundedIcon color="primary" />
                  <Box>
                    <Typography fontWeight={750}>API token</Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      Send in the Authorization header as a Bearer token.
                    </Typography>
                  </Box>
                </Stack>
              </Card>
              <Card elevation={0} sx={{ ...cardSx, p: 2.5 }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <KeyRoundedIcon color="primary" />
                  <Box>
                    <Typography fontWeight={750}>API code</Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      Send in the JSON body as <code>apiCode</code>.
                    </Typography>
                  </Box>
                </Stack>
              </Card>
            </Box>

            <CodeBlock language="http" label="Authorization header">
              {`Authorization: Bearer YOUR_API_TOKEN`}
            </CodeBlock>
            <Alert severity="warning" sx={{ mt: 2.5, borderRadius: 2 }}>
              Keep both credentials on your server. Do not place them in
              browser-side code or public repositories.
            </Alert>
          </Box>

          <Box>
            <SectionTitle
              id="send-campaign"
              eyebrow="Endpoint"
              description="Queue an approved template message for every phone number in the request."
            >
              Send a campaign
            </SectionTitle>

            <Card elevation={0} sx={{ ...cardSx, p: { xs: 2, sm: 2.5 } }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ sm: "center" }}
              >
                <Chip
                  label="POST"
                  color="primary"
                  sx={{ fontWeight: 800, alignSelf: "flex-start" }}
                />
                {isLoading ? (
                  <Skeleton width="70%" height={32} />
                ) : (
                  <Box
                    component="code"
                    sx={{
                      minWidth: 0,
                      overflowWrap: "anywhere",
                      fontSize: { xs: "0.8rem", sm: "0.9rem" },
                    }}
                  >
                    {campaignEndpoint}
                  </Box>
                )}
              </Stack>
            </Card>
          </Box>

          <Box>
            <SectionTitle
              id="parameters"
              eyebrow="Schema"
              description="Only send conditional fields that correspond to components defined by the approved template."
            >
              Request parameters
            </SectionTitle>
            <ParameterTable rows={requestParameters} />
          </Box>

          <Box>
            <SectionTitle
              id="examples"
              eyebrow="Quickstart"
              description="Select the template structure closest to yours, then choose a client language."
            >
              Request examples
            </SectionTitle>

            <Box
              sx={{ borderBottom: "1px solid", borderColor: "divider", mb: 3 }}
            >
              <Tabs
                value={payloadPreset}
                onChange={(_, value) => setPayloadPreset(value)}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="Campaign payload examples"
              >
                {payloadPresets.map((preset) => (
                  <Tab
                    key={preset.key}
                    value={preset.key}
                    label={preset.label}
                  />
                ))}
              </Tabs>
            </Box>

            <Typography variant="h6" fontWeight={700}>
              {selectedPreset.title}
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5, mb: 2.5 }}>
              {selectedPreset.description}
            </Typography>

            <Tabs
              value={codeLanguage}
              onChange={(_, value) => setCodeLanguage(value)}
              sx={{ minHeight: 42, mb: 1 }}
            >
              <Tab value="curl" label="cURL" sx={{ minHeight: 42 }} />
              <Tab
                value="javascript"
                label="JavaScript"
                sx={{ minHeight: 42 }}
              />
              <Tab value="node" label="Node.js" sx={{ minHeight: 42 }} />
            </Tabs>
            <CodeBlock
              language={codeLanguage === "curl" ? "bash" : "javascript"}
              label={
                codeLanguage === "curl"
                  ? "cURL"
                  : codeLanguage === "node"
                  ? "Node.js + Axios"
                  : "JavaScript + Fetch"
              }
            >
              {codeSamples[codeLanguage]}
            </CodeBlock>
          </Box>

          <Box>
            <SectionTitle
              id="template-rules"
              eyebrow="Template matching"
              description="The API maps your arrays to the components of the approved template. Values must match its structure and order."
            >
              Template parameter rules
            </SectionTitle>

            <Stack spacing={1.5}>
              {[
                [
                  "Text header",
                  "Pass headerParams. The first array value is used for the header variable.",
                ],
                [
                  "Media header",
                  "Pass mediaUrl for image, video, or document headers. The URL must be publicly accessible to WhatsApp.",
                ],
                [
                  "Body variables",
                  "Pass bodyParams in the same order as the placeholders in the approved template.",
                ],
                [
                  "Document header",
                  "Pass filename when you want WhatsApp to display a specific document name.",
                ],
                [
                  "Dynamic URL button",
                  "Pass buttonParams when a URL button has a variable suffix. The first value is used.",
                ],
                [
                  "Authentication template",
                  "Pass the OTP as bodyParams[0]. It is used for the body and authentication button.",
                ],
              ].map(([title, description], index) => (
                <Card key={title} elevation={0} sx={{ ...cardSx, p: 2.25 }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Box
                      sx={{
                        display: "grid",
                        placeItems: "center",
                        width: 30,
                        height: 30,
                        flexShrink: 0,
                        borderRadius: "50%",
                        color: "primary.dark",
                        bgcolor: "rgba(18, 140, 126, 0.10)",
                        fontWeight: 800,
                        fontSize: "0.8rem",
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Box>
                      <Typography fontWeight={700}>{title}</Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.4 }}
                      >
                        {description}
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              ))}
            </Stack>
          </Box>

          <Box>
            <SectionTitle
              id="response"
              eyebrow="HTTP 200"
              description="A successful response confirms that every recipient was stored and added to the delivery queue. It does not confirm final WhatsApp delivery."
            >
              Response
            </SectionTitle>
            <CodeBlock language="json" label="200 OK">
              {`{
  "message": "Campaign sent successfully!",
  "wabaId": "123456789012345"
}`}
            </CodeBlock>

            <Alert
              icon={<QueueRoundedIcon />}
              severity="info"
              sx={{ mt: 2.5, borderRadius: 2 }}
            >
              Messages start with a <code>queued</code> state. Background
              workers submit them to WhatsApp, update their provider message ID
              and status, and debit the wallet per successful send.
            </Alert>
          </Box>

          <Box>
            <SectionTitle
              id="errors"
              eyebrow="Troubleshooting"
              description="Campaign errors return a JSON object with a message field. Inspect both the HTTP status and message."
            >
              Errors
            </SectionTitle>

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2.5}
              sx={{ mb: 3 }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <CodeBlock language="json" label="401 Unauthorized">
                  {`{
  "message": "The api code is not correct."
}`}
                </CodeBlock>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <CodeBlock language="json" label="400 Bad Request">
                  {`{
  "message": "Insufficient Balance. Your current balance is 10.00. Required balance: 15.00"
}`}
                </CodeBlock>
              </Box>
            </Stack>

            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ boxShadow: "none" }}
            >
              <Table sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Condition</TableCell>
                    <TableCell>Meaning</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {errorResponses.map(([status, condition, meaning]) => (
                    <TableRow key={`${status}-${condition}`}>
                      <TableCell>
                        <Chip
                          size="small"
                          label={status}
                          color={status === "401" ? "default" : "error"}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700}>
                          {condition}
                        </Typography>
                      </TableCell>
                      <TableCell>{meaning}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
            }}
          >
            <Card
              elevation={0}
              sx={{
                ...cardSx,
                p: 2.5,
                bgcolor: "rgba(255, 171, 0, 0.055)",
                borderColor: "rgba(255, 171, 0, 0.35)",
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <WarningAmberRoundedIcon color="warning" />
                <Box>
                  <Typography fontWeight={750}>
                    Validate before sending
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    Confirm the template is approved and parameter counts match
                    before submitting a large recipient list.
                  </Typography>
                </Box>
              </Stack>
            </Card>
            <Card
              elevation={0}
              sx={{
                ...cardSx,
                p: 2.5,
                bgcolor: "rgba(18, 140, 126, 0.045)",
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <LocalAtmRoundedIcon color="primary" />
                <Box>
                  <Typography fontWeight={750}>Check your balance</Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    The request is rejected when the wallet cannot cover the
                    estimated cost of all recipients.
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Box>

          <Divider />
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ sm: "center" }}
            spacing={1}
            sx={{ pb: 3 }}
          >
            <Typography variant="body2" color="text.secondary">
              Campaign Messaging API · REST · JSON
            </Typography>
            <Link
              href="#overview"
              underline="hover"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
            >
              Back to top
              <ArrowForwardRoundedIcon
                sx={{ transform: "rotate(-90deg)", fontSize: 18 }}
              />
            </Link>
          </Stack>
        </Stack>
      </Box>
    </Container>
  );
};

export default CampaignApiDoc;
